import React from "react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { z } from "zod";

import { PandaQuizQuestionSchema } from "@/lib/zod/schemas/job";

// Helper functions for consistency with QuestionCard
function getRelevancyColor(relevancy: number): [number, number, number] {
  switch (relevancy) {
    case 10:
      return [39, 174, 96]; // Green
    case 8:
      return [41, 128, 185]; // Blue
    case 6:
      return [243, 156, 18]; // Yellow
    case 4:
      return [230, 126, 34]; // Orange
    default:
      return [189, 195, 199]; // Gray
  }
}

function getRelevancyText(relevancy: number): string {
  switch (relevancy) {
    case 10:
      return "Best Answer";
    case 8:
      return "Good Answer";
    case 6:
      return "Acceptable";
    case 4:
      return "Less Relevant";
    default:
      return "Not Relevant";
  }
}

function getRelevancyBackgroundColor(
  relevancy: number
): [number, number, number] {
  switch (relevancy) {
    case 10:
      return [230, 255, 230]; // Light green
    case 8:
      return [230, 240, 255]; // Light blue
    case 6:
      return [255, 250, 220]; // Light yellow
    case 4:
      return [255, 235, 220]; // Light orange
    default:
      return [245, 245, 245]; // Light gray
  }
}

// Process tags for display
function formatTags(tags: any[]): string {
  if (!tags || !Array.isArray(tags)) return "";

  return tags
    .map((tag) => (typeof tag === "string" ? tag : tag.name))
    .filter(Boolean)
    .join(", ");
}

const DownloadPdf = async (
  questions: z.infer<typeof PandaQuizQuestionSchema>[],
  title?: string
) => {
  try {
    // Check if questions exist
    if (questions.length === 0) {
      toast.error("No questions to export");
      return;
    }

    // Create a new PDF document
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Document dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    // Style variables
    const primaryColor = [0, 0, 0]; // Black
    const secondaryColor = [120, 120, 120]; // Gray
    const accentColor = [41, 128, 185]; // Blue
    const skillTagColor = [41, 128, 185]; // Blue for skill tags

    // Helper functions for drawing
    const addHeader = (pdf, pageNum, totalPages) => {
      // Try to load logo from different sources with fallbacks
      const logoSources = [
        "/images/logo.png", // Fallback to Next.js public folder
      ];

      // Logo placement
      let logoAdded = false;

      // Try each source until one works
      const addLogoFromSources = async (sources) => {
        for (const source of sources) {
          try {
            pdf.addImage(source, "PNG", margin, 10, 12, 12);
            logoAdded = true;
            break;
          } catch (e) {
            console.warn(
              `Failed to load logo from ${source}, trying next source...`
            );
            continue;
          }
        }

        // If all sources fail, just proceed without logo
        if (!logoAdded) {
          console.warn(
            "Could not load logo from any source, proceeding without logo"
          );
        }
      };

      // Attempt to add the logo
      try {
        addLogoFromSources(logoSources);
      } catch (e) {
        console.warn("Error adding logo:", e);
      }

      // Header text
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor(...primaryColor);
      pdf.text("Panda Quiz", margin + (logoAdded ? 15 : 0), 18)

      // Date
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(...secondaryColor);
      pdf.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        pageWidth - margin - 40,
        10
      );

      // Page number
      pdf.text(
        `Page ${pageNum} of ${totalPages}`,
        pageWidth - margin - 20,
        pageHeight - 10
      );

      // Divider line
      pdf.setDrawColor(...accentColor);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 22, pageWidth - margin, 22);
    };

    // Try to add watermark (with error handling)
    const addWatermark = (pdf) => {
      try {
        // Try both provided URL and fallback
        const watermarkSources = ["/images/wordmark.png"];

        for (const source of watermarkSources) {
          try {
            pdf.setGState(pdf.GState({ opacity: 0.1 }));
            pdf.addImage(
              source,
              "PNG",
              pageWidth / 2 - 35,
              pageHeight / 2 - 15,
              70,
              30
            );
            pdf.setGState(pdf.GState({ opacity: 1.0 }));
            return true; // Successfully added watermark
          } catch (e) {
            console.warn(
              `Failed to add watermark from ${source}, trying next source...`
            );
          }
        }

        // If we couldn't add the image watermark, fall back to text
        pdf.setTextColor(200, 200, 200);
        pdf.setFontSize(30);
        pdf.setFont("helvetica", "bold");
        pdf.setGState(pdf.GState({ opacity: 0.1 }));
        pdf.text("Panda Quiz", pageWidth / 2 - 30, pageHeight / 2, {
          align: "center",
        });
        pdf.setGState(pdf.GState({ opacity: 1.0 }));

        return true;
      } catch (e) {
        console.warn("Could not add watermark:", e);
        return false;
      }
    };

    // Render a single question to the PDF
    const renderQuestion = (pdf, question, yPosition, questionNumber) => {
      const startY = yPosition;
      let currentY = startY;
      const lineHeight = 5;

      // Question number circle
      pdf.setFillColor(...primaryColor);
      pdf.circle(margin + 2.5, currentY + 5, 3.5, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(255, 255, 255);
      pdf.text(questionNumber.toString(), margin + 2.5, currentY + 6.5, {
        align: "center",
      });

      // Question title
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(...primaryColor);

      // Handle multiline question text
      const questionLines = pdf.splitTextToSize(
        question.question,
        contentWidth - 10
      );
      pdf.text(questionLines, margin + 8, currentY + 5);
      currentY += questionLines.length * 6 + 2;

      // Metadata row - display the same metadata as in QuestionCard
      currentY += 5;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(...secondaryColor);

      // Display metadata in a nicely formatted layout
      let metadataX = margin + 5;

      // SkillName if available
      if (question.skillName) {
        pdf.text("Skill:", metadataX, currentY);
        metadataX += 10;
        pdf.setTextColor(...skillTagColor);
        pdf.text(question.skillName, metadataX, currentY);
        metadataX += pdf.getTextWidth(question.skillName) + 10;
        pdf.setTextColor(...secondaryColor);
      }

      // Experience level
      pdf.text("Experience:", metadataX, currentY);
      metadataX += 18;
      pdf.setTextColor(...skillTagColor);

      // Use the appropriate experience level field
      const expLevel =
        question.experienceLevel || question.experience_level || "";
      pdf.text(expLevel.toString(), metadataX, currentY);
      metadataX += pdf.getTextWidth(expLevel.toString()) + 10;
      pdf.setTextColor(...secondaryColor);

      // Skill Tags
      if (question.skillTags && question.skillTags.length > 0) {
        pdf.text("Skills:", metadataX, currentY);
        metadataX += 12;

        const skillTagsText = formatTags(question.skillTags);
        pdf.setTextColor(...skillTagColor);
        pdf.text(skillTagsText, metadataX, currentY);
        metadataX += pdf.getTextWidth(skillTagsText) + 10;
        pdf.setTextColor(...secondaryColor);
      }

      // Taxonomy Tags
      const taxonomyTags = question.taxonomyTags || question.bloomsTaxonomyTags;
      if (taxonomyTags && taxonomyTags.length > 0) {
        pdf.text("Taxonomy:", metadataX, currentY);
        metadataX += 18;

        const taxonomyText = formatTags(taxonomyTags);
        pdf.setTextColor(...skillTagColor);
        pdf.text(taxonomyText, metadataX, currentY);
        pdf.setTextColor(...secondaryColor);
      }

      currentY += lineHeight;

      // Options
      if (question.options) {
        currentY += 3;

        // Draw options in a 2x2 grid if space permits
        const optionsPerRow = contentWidth >= 160 ? 2 : 1;
        const optionWidth = (contentWidth - 10) / optionsPerRow;

        for (let i = 0; i < question.options.length; i += optionsPerRow) {
          const rowY = currentY;

          for (
            let j = 0;
            j < optionsPerRow && i + j < question.options.length;
            j++
          ) {
            const option = question.options[i + j];
            const optionX = margin + 5 + j * optionWidth;
            let optionY = rowY;

            // Map the relevancy to 4, 6, 8, or 10 if needed
            const relevancy = option.relevancy;
            const mappedRelevancy = relevancy <= 1 ? relevancy * 10 : relevancy;

            // Get colors and text based on relevancy - using same helper functions as QuestionCard
            const bgColor = getRelevancyBackgroundColor(mappedRelevancy);
            const barColor = getRelevancyColor(mappedRelevancy);
            const relevancyText = getRelevancyText(mappedRelevancy);

            // Draw rounded rectangle for option
            pdf.setFillColor(...bgColor);
            pdf.setDrawColor(200, 200, 200);
            pdf.roundedRect(
              optionX,
              optionY,
              optionWidth - 5,
              32, // Increased height to accommodate skill tags
              2,
              2,
              "FD"
            );

            // Option letter
            pdf.setFillColor(...barColor);
            pdf.circle(optionX + 4, optionY + 4, 3, "F");
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(6);
            pdf.setTextColor(255, 255, 255);
            pdf.text(
              String.fromCharCode(65 + i + j),
              optionX + 4,
              optionY + 5.5,
              { align: "center" }
            );

            // Option text
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(9);
            pdf.setTextColor(...primaryColor);

            const optionText = pdf.splitTextToSize(
              option.option,
              optionWidth - 15
            );
            pdf.text(optionText, optionX + 9, optionY + 4)

            // Calculate text height to adjust following elements
            const textHeight = Math.max(optionText.length * 3, 10);

            const min = option.experienceRange?.min ?? 0;
            const max = option.experienceRange?.max ?? 0;
            
            // Margin for experience
            const experienceMarginTop = 6;
            const experienceMarginBottom = 4;
            const experienceY = optionY + textHeight + experienceMarginTop;

            // Sort values to ensure smaller value comes first
            const [smaller, larger] = [min, max].sort((a, b) => a - b);
            

            // Experience range
            pdf.setFontSize(7);
            pdf.setTextColor(...secondaryColor);
            if (smaller === larger) {
              if (smaller === 0) {
                pdf.text(
                  `Experience: Fresher`,
                  optionX + 9,
                  experienceY
                );
              } else if (smaller === 1) {
                pdf.text(
                  `Experience: ${smaller} year`,
                  optionX + 9,
                  experienceY
                );
              } else {
                pdf.text(
                  `Experience: ${smaller} years`,
                  optionX + 9,
                  experienceY
                );
              }
            } else {
              // Range case
              pdf.text(
                `Experience: ${smaller}-${larger} yrs`,
                optionX + 9,
                experienceY
              );
            }

            let currentTextY = experienceY + experienceMarginBottom;

            // Option skill tags
            if (option.skillTags && option.skillTags.length > 0) {
              pdf.setTextColor(...skillTagColor);
              const skillText = formatTags(option.skillTags);
              pdf.text(skillText, optionX + 9, currentTextY);
              pdf.setTextColor(...secondaryColor);
              // Update currentTextY only if skill tags exist
              currentTextY += 4;
            }

            // Relevancy text
            pdf.setFontSize(7);
            pdf.text(relevancyText, optionX + 9, currentTextY);

            // Relevancy score
            const scoreText = `Relevancy: ${mappedRelevancy} | Score: ${option.score}`;
            pdf.text(
              scoreText,
              optionX + optionWidth - 7 - pdf.getTextWidth(scoreText),
              currentTextY
            );

            // Move down for the bar
            currentTextY += 2;

            // Relevancy bar
            const barWidth = 30;
            pdf.setFillColor(220, 220, 220);
            pdf.rect(optionX + 9, currentTextY, barWidth, 2, "F");

            pdf.setFillColor(...barColor);
            pdf.rect(
              optionX + 9,
              currentTextY,
              barWidth * (mappedRelevancy / 10),
              2,
              "F"
            );
          }

          currentY += 35; // Increased height of option box + margin
        }
      }

      // Draw divider line
      currentY += 5;
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.2);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 10;

      return currentY; // Return the new Y position
    };

    // Split questions into pages
    let currentPage = 1;
    let yPosition = 30; // Start position after header
    const questionHeights: number[] = []; // Estimate heights for planning

    // Calculate approximate heights for planning
    questions.forEach((question) => {
      // Base height for question
      let height = 10;

      // Add height for question text (assuming ~5mm per line)
      const questionLines = pdf.splitTextToSize(
        question.question,
        contentWidth - 10
      ).length;
      height += questionLines * 6;

      // Add height for metadata
      height += 10;

      // Add height for options (assuming 2x2 grid with 35mm per row)
      height += Math.ceil(question.options.length / 2) * 35;

      // Add margins and divider
      height += 15;

      questionHeights.push(height);
    });

    // Calculate the total number of pages
    let plannedPages = 1;
    let runningHeight = 0;

    questionHeights.forEach((height) => {
      if (runningHeight + height > pageHeight - 50) {
        // Reserve space for header/footer
        plannedPages++;
        runningHeight = height;
      } else {
        runningHeight += height;
      }
    });

    // Start rendering questions
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];

      // Check if we need a new page
      if (yPosition + questionHeights[i] > pageHeight - 20) {
        // Add watermark to current page before creating a new one
        addWatermark(pdf);

        // Add new page
        pdf.addPage();
        currentPage++;
        yPosition = 30; // Reset Y position

        // Add header to the new page
        addHeader(pdf, currentPage, plannedPages);
      }

      // Render this question
      const newYPosition = renderQuestion(pdf, question, yPosition, i + 1);
      yPosition = newYPosition;
    }

    // Add watermark to the last page
    addWatermark(pdf);

    // Add header to the first page (after we know the total number of pages)
    if (currentPage >= 1) {
      pdf.setPage(1);
      addHeader(pdf, 1, plannedPages);
    }

    try {
      // Get blob from the pdf
      const pdfBlob = pdf.output("blob");

      // Create a download link and trigger it
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(pdfBlob);
      downloadLink.download = `${title || "pandaquiz"}.pdf`;

      // Append to the document, click, and remove
      document.body.appendChild(downloadLink);
      downloadLink.click();

      // Small delay before removing to ensure the download starts
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadLink.href);
      }, 100);

      toast.success("PDF downloaded successfully!");
    } catch (downloadError) {
      console.error("Error triggering download:", downloadError);

      // Fallback method if the above fails
      try {
        pdf.save(`${title || "pandaquiz"}.pdf`);
        toast.success("PDF downloaded (fallback method)");
      } catch (fallbackError) {
        console.error("Fallback download failed:", fallbackError);
        toast.error("Download failed. Try again or check console for details.");
      }
    }
  } catch (error) {
    console.error("PDF download error:", error);
    toast.error("Failed to download PDF");
  }
};

export default DownloadPdf;
